import { GroupCustomSegment, GroupRole } from '@libs/src/models/group.model';
import {
  MatchConfigCustomSegmentConfig,
  MatchSegment,
} from '@libs/src/models/match.interfaces';
import { EmptyObject } from '@libs/src/utilities/types.utilities';
import { UploadItem } from '@main-client/src/app/upload-csv-list-drawer/upload-csv-list-drawer';

import concat from 'lodash-es/concat';
import difference from 'lodash-es/difference';
import differenceWith from 'lodash-es/differenceWith';
import find from 'lodash-es/find';
import findIndex from 'lodash-es/findIndex';
import groupBy from 'lodash-es/groupBy';
import head from 'lodash-es/head';
import includes from 'lodash-es/includes';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import reduce from 'lodash-es/reduce';
import set from 'lodash-es/set';
import some from 'lodash-es/some';
import uniq from 'lodash-es/uniq';

export interface CustomSegmentCsvRow {
  0: string;
  1: string;
}

export interface MatchSegmentCsvRow {
  value: string;
  match_with: string;
}

export function buildAddEdgesUpdateFromCsvRows(
  csvRows: CustomSegmentCsvRow[],
  customSegment: GroupCustomSegment,
  existingEdges: string[][],
) {
  const potentialEdgesToAdd = convertCsvRowsToEdges(csvRows);
  const uniqueEdgesToAdd = getUniqueEdgesFromExistingEdges(
    potentialEdgesToAdd,
    existingEdges,
  ).reduce((resultEdges: string[][], edge: string[]) => {
    if (edgeIsUnique(edge, resultEdges)) {
      return [...resultEdges, edge];
    }
    return resultEdges;
  }, []);
  const nodes = getNodeNamesFromCustomSegment(customSegment);
  return uniqueEdgesToAdd.filter(
    (edge: string[]) => 0 === getNonExistentNodesFromEdge(edge, nodes).length,
  );
}

export function buildAddEdgesUploadResultsFromCsvRows(
  csvRows: CustomSegmentCsvRow[],
  customSegment: GroupCustomSegment,
  existingEdges: string[][],
) {
  const nodes = getNodeNamesFromCustomSegment(customSegment);
  return csvRows.map((csvRow: CustomSegmentCsvRow) => {
    const edge = convertCsvRowToEdge(csvRow);
    const nonExistingNodes = getNonExistentNodesFromEdge(edge, nodes);
    if (0 < nonExistingNodes.length) {
      const error =
        1 < nonExistingNodes.length
          ? `${nonExistingNodes.join(', ')} do not exist`
          : `${nonExistingNodes[0]} does not exist`;
      return {
        error,
        value: csvRow,
      };
    }
    if (!edgeIsUnique(edge, existingEdges)) {
      return { error: 'Introduction rule already exists', value: csvRow };
    }
    return { value: csvRow };
  });
}

export function buildDeleteEdgesUpdateFromCsvRows(
  csvRows: CustomSegmentCsvRow[],
  customSegment: any,
  existingEdges: string[][],
) {
  const potentialEdgesToDelete = convertCsvRowsToEdges(csvRows);
  const uniqueEdgesToDelete = getUniqueEdgesFromExistingEdges(
    existingEdges,
    potentialEdgesToDelete,
  );
  const nodes = getNodeNamesFromCustomSegment(customSegment);
  return uniqueEdgesToDelete.filter(
    (edge: string[]) => 0 === getNonExistentNodesFromEdge(edge, nodes).length,
  );
}

export function buildDeleteEdgesUploadResultsFromCsvRows(
  csvRows: CustomSegmentCsvRow[],
  customSegment: GroupCustomSegment,
  existingEdges: string[][],
) {
  const nodes = getNodeNamesFromCustomSegment(customSegment);
  return csvRows.map((csvRow: CustomSegmentCsvRow) => {
    const edge = convertCsvRowToEdge(csvRow);
    const nonExistingNodes = getNonExistentNodesFromEdge(edge, nodes);
    if (0 < nonExistingNodes.length) {
      const error =
        1 < nonExistingNodes.length
          ? `${nonExistingNodes.join(', ')} do not exist`
          : `${nonExistingNodes[0]} does not exist`;
      return {
        error,
        value: csvRow,
      };
    }
    if (edgeIsUnique(edge, existingEdges)) {
      return { error: 'Introduction rule does not exist', value: csvRow };
    }
    return { value: csvRow };
  });
}

export function convertCsvRowsToEdges(csvRows: CustomSegmentCsvRow[]) {
  return csvRows.map((csvRow: CustomSegmentCsvRow) =>
    convertCsvRowToEdge(csvRow),
  );
}

export function convertCsvRowToEdge(csvRow: CustomSegmentCsvRow) {
  return reduce(
    csvRow,
    (edge, value, key) => set(edge, key.trim(), value.trim()),
    [],
  );
}

export function edgeIsSame(edge: string[], edgeToCompare: string[]) {
  return (
    isEqual(edgeToCompare, edge) || isEqual(edgeToCompare, [...edge].reverse())
  );
}

export function edgeIsUnique(edge: string[], edges: string[][]) {
  return !some(edges, (edgeToCompare: string[]) =>
    edgeIsSame(edge, edgeToCompare),
  );
}

export function findCustomSegmentConfigById(
  customSegmentId: string | undefined,
  customSegmentConfigs: MatchConfigCustomSegmentConfig[],
) {
  return find(
    customSegmentConfigs,
    (config: MatchConfigCustomSegmentConfig) =>
      customSegmentId === config.custom_segment,
  );
}

export function getNodeNamesFromCustomSegment(
  customSegment: GroupCustomSegment,
) {
  return customSegment?.nodes?.map((node: { name: string }) => node.name) || [];
}

export function getNonExistentNodesFromEdge(edge: string[], nodes: string[]) {
  return uniq(edge.filter((node) => !includes(nodes, node)));
}

export function getUniqueEdgesFromExistingEdges(
  edgesToAdd: string[][],
  existingEdges: string[][],
) {
  return differenceWith(edgesToAdd, existingEdges, edgeIsSame);
}

export function findMatchSegmentByRole(
  matchSegments: MatchSegment[],
  role: string,
) {
  return find(matchSegments, { value: role.trim() });
}

export function buildAddMatchSegmentsUploadResultsFromCsvRows(
  csvRows: MatchSegmentCsvRow[],
  roles: GroupRole[],
  matchSegments: MatchSegment[],
): UploadItem<MatchSegmentCsvRow>[] {
  return csvRows.map((csvRow: MatchSegmentCsvRow) => {
    const sanitizedCsvRowValue = (csvRow.value || '').trim();
    const sanitizedCsvRowMatchWith = (csvRow.match_with || '').trim();
    const roleNames = roles.map((role: GroupRole) => role.name);
    const nonExistingRoles = uniq([
      sanitizedCsvRowValue,
      sanitizedCsvRowMatchWith,
    ]).filter((roleName: string) => !includes(roleNames, roleName.trim()));
    if (0 < nonExistingRoles.length) {
      const error =
        1 < nonExistingRoles.length
          ? `${nonExistingRoles.join(', ')} do not exist`
          : `${nonExistingRoles[0]} does not exist`;
      return {
        error,
        value: csvRow,
      };
    }
    const matchSegmentMatchWithValues =
      findMatchSegmentByRole(matchSegments, sanitizedCsvRowValue)?.match_with ||
      [];
    if (includes(matchSegmentMatchWithValues, sanitizedCsvRowMatchWith)) {
      return { error: 'Introduction rule already exists', value: csvRow };
    }
    return {
      value: {
        match_with: sanitizedCsvRowMatchWith,
        value: sanitizedCsvRowValue,
      },
    };
  });
}

export function buildDeleteMatchSegmentsUploadResultsFromCsvRows(
  csvRows: MatchSegmentCsvRow[],
  roles: GroupRole[],
  matchSegments: MatchSegment[],
) {
  return csvRows.map((csvRow: MatchSegmentCsvRow) => {
    const sanitizedCsvRowValue = (csvRow.value || '').trim();
    const sanitizedCsvRowMatchWith = (csvRow.match_with || '').trim();
    const roleNames = roles.map((role: GroupRole) => role.name);
    const nonExistingRoles = uniq([
      sanitizedCsvRowValue,
      sanitizedCsvRowMatchWith,
    ]).filter((roleName: string) => !includes(roleNames, roleName.trim()));
    if (0 < nonExistingRoles.length) {
      const error =
        1 < nonExistingRoles.length
          ? `${nonExistingRoles.join(', ')} do not exist`
          : `${nonExistingRoles[0]} does not exist`;
      return {
        error,
        value: csvRow,
      };
    }
    const matchSegmentMatchWithValues =
      findMatchSegmentByRole(matchSegments, sanitizedCsvRowValue)?.match_with ||
      [];
    if (!includes(matchSegmentMatchWithValues, sanitizedCsvRowMatchWith)) {
      return { error: 'Introduction rule does not exist', value: csvRow };
    }
    return {
      value: {
        match_with: sanitizedCsvRowMatchWith,
        value: sanitizedCsvRowValue,
      },
    };
  });
}

export function buildAddMatchSegmentUpdateForRoleFromCsvRows(
  csvRows: MatchSegmentCsvRow[],
  matchSegment: MatchSegment | undefined,
  roleName: string,
) {
  const csvRowsForRole = csvRows.filter(
    (csvRow: MatchSegmentCsvRow) => roleName === csvRow.value,
  );
  const matchSegmentBody = head(convertCsvRowsToMatchSegments(csvRowsForRole));
  const matchWith = concat(
    matchSegment?.match_with || [],
    matchSegmentBody?.match_with || [],
  );
  if (isEmpty(matchWith)) {
    return {};
  }
  return {
    ...matchSegment,
    ...matchSegmentBody,
    match_with: matchWith,
  };
}

export function buildDeleteMatchSegmentUpdateForRoleFromCsvRows(
  csvRows: MatchSegmentCsvRow[],
  matchSegment: MatchSegment,
  roleName: string,
): MatchSegment | EmptyObject<MatchSegment> {
  if (!matchSegment) {
    return {};
  }
  const csvRowsForRole = csvRows.filter(
    (csvRow: MatchSegmentCsvRow) => roleName === csvRow.value,
  );
  const matchSegmentBody = head(convertCsvRowsToMatchSegments(csvRowsForRole));
  const matchWith = difference(
    matchSegment?.match_with || [],
    matchSegmentBody?.match_with || [],
  );
  if (isEqual(matchWith, matchSegment.match_with)) {
    return {};
  }
  return {
    ...matchSegment,
    ...matchSegmentBody,
    match_with: matchWith,
  };
}

export function convertCsvRowsToMatchSegments(csvRows: MatchSegmentCsvRow[]) {
  return csvRows.reduce(
    (result, csvRow) => {
      const matchSegmentIndex = findIndex(result, { value: csvRow.value });
      if (-1 < matchSegmentIndex) {
        result[matchSegmentIndex].match_with.push(csvRow.match_with);
      } else {
        result.push(convertCsvRowToMatchSegment(csvRow));
      }
      return result;
    },
    [] as { value: string; match_with: string[] }[],
  );
}

export function convertCsvRowToMatchSegment(csvRow: MatchSegmentCsvRow) {
  return {
    match_with: [csvRow.match_with],
    path: 'groups.role',
    value: csvRow.value,
  };
}

export function getValidCsvRowsGroupedByRole<T extends { value: any }>(
  uploadItems: UploadItem<T>[],
) {
  const csvRows = uploadItems
    .filter((item: UploadItem) => !item.error)
    .map((item: UploadItem) => item.value);
  return groupBy<T>(csvRows, (csvRole) => csvRole.value);
}
